import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Slide,
  SwipeableDrawer
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ZeiterfassungSelectDatum from './ZeiterfassungSelectDatum';
import ZeiterfassungSelectDatumErfassteZeiten from './ZeiterfassungSelectDatumErfassteZeiten';
import ZeiterfassungContextProvider from '../../../../Contexts/ZeiterfassungContext/ZeiterfassungContextProvider';
import ZeiterfassungContext from '../../../../Contexts/ZeiterfassungContext/ZeiterfassungContext';
import ZeiterfassungNeueZeitmeldung from './ZeiterfassungNeueZeitmeldung';
import UserCalendar from './UserCalendar';

// TabPanel component to handle the content of each tab
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`zeiterfassung-tabpanel-${index}`}
      aria-labelledby={`zeiterfassung-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none', width: '100%' }}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// Generate a11y props for tabs
function a11yProps(index: number) {
  return {
    id: `zeiterfassung-tab-${index}`,
    'aria-controls': `zeiterfassung-tabpanel-${index}`,
  };
}


// Modified CalendarButton component that works with tabs
const CalendarTabButton = ({ onClick }: { onClick: () => void }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Button
      variant="outlined"
      startIcon={<CalendarMonthIcon />}
      onClick={onClick}
      sx={{
        ml: {xs: 0, sm: 2},
        mt: {xs: 1, sm: 0},
        textTransform: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        borderColor: 'rgba(25, 118, 210, 0.5)',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
        },
        width: isMobile ? '100%' : 'auto'
      }}
      size={isMobile ? 'medium' : 'small'}>
      Kalender anzeigen
    </Button>
  );
};

export default function Zeiterfassung() {
  const [tabValue, setTabValue] = React.useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Switch to calendar tab
  const handleShowCalendar = () => {
    setTabValue(1);
  };

  // Switch back to arbeitszeiten tab
  const handleBackToArbeitszeiten = () => {
    setTabValue(0);
  };

  return (
    <ZeiterfassungContextProvider>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        position: 'relative'
      }}>
        {/* Mobile: Calendar button */}
        {isSmall && tabValue === 0 && (
          <Box sx={{ 
            px: 1,
            mb: 2
          }}>
            <CalendarTabButton onClick={handleShowCalendar} />
          </Box>
        )}

        {/* Mobile: Back button when in calendar view */}
        {isSmall && tabValue === 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            px: 1, 
            mb: 2,
            mt: 1 
          }}>
            <Button
              variant="outlined"
              size="medium"
              onClick={handleBackToArbeitszeiten}
              startIcon={<AccessTimeIcon />}
              sx={{ 
                textTransform: 'none',
                width: '100%'
              }}
            >
              Zurück zu Arbeitszeiten
            </Button>
          </Box>
        )}

        {/* Tabs for larger screens */}
        {!isSmall && (
          <Box sx={{ width: '100%' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="Zeiterfassung tabs"
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'medium',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }
              }}
            >
              <Tab 
                icon={<AccessTimeIcon />} 
                iconPosition="start" 
                label="Arbeitszeiten" 
                {...a11yProps(0)} 
              />
              <Tab 
                icon={<CalendarMonthIcon />} 
                iconPosition="start" 
                label="Kalender" 
                {...a11yProps(1)} 
              />
            </Tabs>
          </Box>
        )}

        {/* Tab panels container - full screen */}
        <Box sx={{ 
          flex: 1,
          width: '100%',
          overflow: 'auto',
          p: 1
        }}>
          <TabPanel value={tabValue} index={0}>
            <ZeiterfassungSelectDatum />
            <ZeiterfassungSelectDatumErfassteZeiten />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Mobile navigation is now handled outside this component */}
            <UserCalendar onClose={handleBackToArbeitszeiten} />
          </TabPanel>
        </Box>

        {/* The ZeiterfassungNeueZeitmeldung content will be rendered inside a dialog */}
        <ZeiterfassungDialog />
      </Box>
    </ZeiterfassungContextProvider>
  );
}

// Separate component for the dialog functionality
function ZeiterfassungDialog() {
  const {
    createNeueZeitmeldung,
    setCreateNeueZeitmeldung,
    editMode,
    resetForm,
    setEditMode,
    setCurrentEditId,
  } = React.useContext(ZeiterfassungContext);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Handle dialog close
  const handleClose = () => {
    setCreateNeueZeitmeldung(false);
    resetForm();
    if (editMode) {
      setEditMode(false);
      setCurrentEditId(-1);
    }
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      maxWidth="md"
      open={createNeueZeitmeldung}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          ...(fullScreen ? {} : { width: '100%', maxWidth: 700 }),
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          p: 1,
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
        }}>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <ZeiterfassungNeueZeitmeldung />
      </DialogContent>
    </Dialog>
  );
}